import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { ArrowRight, MapPin, Shield, Award } from "lucide-react";

// Animation constants (extracted to prevent inline object creation)
const HERO_INITIAL = { opacity: 0, y: 30 };
const HERO_ANIMATE = { opacity: 1, y: 0 };
const HERO_EMPTY = {};
const HERO_TRANSITION = { duration: 0.8, delay: 0.2 };
const SCROLL_INITIAL = { opacity: 0 };
const SCROLL_ANIMATE = { opacity: 1 };
const SCROLL_TRANSITION = { delay: 1.5 };
const BOUNCE_ANIMATE = { y: [0, 8, 0] };
const BOUNCE_TRANSITION = { repeat: Infinity, duration: 1.5 };

export default function HeroSection({ isLoaded }) {
  const scrollToQuote = () => {
    const element = document.getElementById("quote");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      data-testid="hero-section"
      className="relative min-h-[100vh] flex items-center justify-center overflow-hidden"
    >
      {/* Background Image */}
      <div className="absolute inset-0">
        <img
          src="https://customer-assets.emergentagent.com/job_quality-builds-11/artifacts/1vf4qovi_kiten%20remodel.jpg"
          alt="Kitchen remodel by JV Home Renovation"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-overlay" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-6 md:px-12 py-32 text-center md:text-left">
        <motion.div
          initial={HERO_INITIAL}
          animate={isLoaded ? HERO_ANIMATE : HERO_EMPTY}
          transition={HERO_TRANSITION}
          className="max-w-3xl"
        >
          {/* Badges */}
          <div className="flex flex-wrap gap-4 justify-center md:justify-start mb-6">
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-white/90 bg-white/10 backdrop-blur-sm px-4 py-2 border border-white/20">
              <MapPin size={14} />
              Santaquin & Nearby Areas
            </span>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-white/90 bg-[#166534]/80 backdrop-blur-sm px-4 py-2">
              <Shield size={14} />
              Licensed & Insured
            </span>
            <span className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.15em] font-semibold text-white/90 bg-white/10 backdrop-blur-sm px-4 py-2 border border-white/20">
              <Award size={14} />
              15+ Years Experience
            </span>
          </div>
          
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl tracking-tighter font-medium text-white mb-6 leading-[1.1]">
            Quality Remodels.
            <br />
            <span className="text-[#22C55E]">Masterful Craftsmanship.</span>
          </h1>
          
          <p className="text-lg md:text-xl text-white/80 mb-10 leading-relaxed max-w-2xl">
            From stucco to tile work, we specialize in home renovations that stand the test of time. 
            Your vision, built with precision and care.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center md:justify-start">
            <Button
              onClick={scrollToQuote}
              className="bg-[#166534] hover:bg-[#14532D] text-white px-8 py-6 text-lg rounded-none font-medium group"
              data-testid="hero-cta"
            >
              Schedule Free Quote
              <ArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" size={20} />
            </Button>
            <Button
              onClick={() => document.getElementById("services")?.scrollIntoView({ behavior: "smooth" })}
              variant="outline"
              className="border-2 border-white/30 bg-transparent text-white hover:bg-white/10 hover:border-white/50 px-8 py-6 text-lg rounded-none font-medium"
              data-testid="hero-services-btn"
            >
              View Our Services
            </Button>
          </div>
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <motion.div
        initial={SCROLL_INITIAL}
        animate={SCROLL_ANIMATE}
        transition={SCROLL_TRANSITION}
        className="absolute bottom-8 left-1/2 -translate-x-1/2"
      >
        <div className="w-6 h-10 border-2 border-white/30 rounded-full flex justify-center pt-2">
          <motion.div
            animate={BOUNCE_ANIMATE}
            transition={BOUNCE_TRANSITION}
            className="w-1.5 h-1.5 bg-white/60 rounded-full"
          />
        </div>
      </motion.div>
    </section>
  );
}
